import React, { CSSProperties, forwardRef, Fragment, Ref, useMemo } from 'react';
import {
  ActionId,
  ActionImpl,
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarResults,
  KBarSearch,
  useMatches,
} from 'kbar';
import useFeatureGatingActions from '@components/devTool/kbar/useFeatureGatingActions';

interface Props {
  children: JSX.Element;
}

const kbarStyleConstants = {
  '--background': 'rgb(26,28,30)',
  '--foreground': 'white',
  '--a1': 'rgba(255,255,255, 0.1)',
  '--shadow': '0.125rem 0.125rem 0.125rem 0.063rem rgba(0, 0, 0, 0.2)',
};

const animatorStyle = {
  ...kbarStyleConstants,
  maxWidth: '37.5rem',
  width: '100%',
  background: 'var(--background)',
  color: 'var(--foreground)',
  borderRadius: '0.5rem',
  overflow: 'hidden',
  boxShadow: 'var(--shadow)',
};

const searchStyle = {
  ...kbarStyleConstants,
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  width: '100%',
  boxSizing: 'border-box' as CSSProperties['boxSizing'],
  outline: 'none',
  border: 'none',
  background: 'var(--background)',
  color: 'var(--foreground)',
};

const groupNameStyle = {
  ...kbarStyleConstants,
  padding: '0.5rem 1rem',
  fontSize: '0.625rem',
  textTransform: 'uppercase' as const,
  opacity: 0.5,
};

const ResultItem = forwardRef(
  (
    {
      action,
      active,
      currentRootActionId,
    }: {
      action: ActionImpl;
      active: boolean;
      currentRootActionId: ActionId;
    },
    ref: Ref<HTMLDivElement>,
  ) => {
    const ancestors = useMemo(() => {
      if (!currentRootActionId) return action.ancestors;
      const index = action.ancestors.findIndex((ancestor) => ancestor.id === currentRootActionId);
      // +1 removes the currentRootAction; e.g.
      // if we are on the "Set theme" parent action,
      // the UI should not display "Set theme… > Dark"
      // but rather just "Dark"
      return action.ancestors.slice(index + 1);
    }, [action.ancestors, currentRootActionId]);

    return (
      <div
        ref={ref}
        style={{
          padding: '0.75rem 1rem',
          background: active ? 'var(--a1)' : 'transparent',
          borderLeft: `0.125rem solid ${active ? 'var(--foreground)' : 'transparent'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            fontSize: 14,
          }}
        >
          {action.icon && action.icon}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div>
              {ancestors.length > 0 &&
                ancestors.map((ancestor) => (
                  <Fragment key={ancestor.id}>
                    <span
                      style={{
                        opacity: 0.5,
                        marginRight: 8,
                      }}
                    >
                      {ancestor.name}
                    </span>
                    <span
                      style={{
                        marginRight: 8,
                      }}
                    >
                      &rsaquo;
                    </span>
                  </Fragment>
                ))}
              <span>{action.name}</span>
            </div>
            {action.subtitle && <span style={{ fontSize: 12 }}>{action.subtitle}</span>}
          </div>
        </div>
        {action.shortcut?.length ? (
          <div aria-hidden style={{ display: 'grid', gridAutoFlow: 'column', gap: '0.25rem' }}>
            {action.shortcut.map((sc) => (
              <kbd
                key={sc}
                style={{
                  padding: '0.25rem 0.375rem',
                  background: 'rgba(0 0 0 / .1)',
                  borderRadius: '0.25rem',
                  fontSize: 14,
                }}
              >
                {sc}
              </kbd>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

const RenderResults = () => {
  const { results, rootActionId } = useMatches();

  const onRender = (params: { item: ActionImpl | string; active: boolean }) => {
    if (typeof params.item === 'string') {
      return <div style={groupNameStyle}>{params.item}</div>;
    }
    return <ResultItem action={params.item} active={params.active} currentRootActionId={rootActionId!} />;
  };

  return <KBarResults items={results} onRender={onRender} />;
};

const CommandBar = () => {
  useFeatureGatingActions();
  return (
    <KBarPortal>
      <KBarPositioner>
        <KBarAnimator style={animatorStyle}>
          <KBarSearch style={searchStyle} />
          <RenderResults />
        </KBarAnimator>
      </KBarPositioner>
    </KBarPortal>
  );
};

const KbarWrapper = ({ children }: Props) => (
  <KBarProvider>
    <CommandBar />
    {children}
  </KBarProvider>
);

export default KbarWrapper;
